/************************************************************************************************************
Author                          : Trizent Technologies Private Limited
Date                            : 
Notes/Remarks                   : 

Organization:  	Trizent Technologies Private Limited.
		Module No. 17, SBC, Thejaswini Building
		Phase 1, Technopark, Thiruvananthapuram - 695581, 
		Kerala, India
 
Copyright 2026 (C) Trizent Technologies Pvt Ltd. (www.trizentinc.com)
All rights reserved.
***************************************************************************************************************/

import { useNavigate, useParams } from "react-router-dom";
import { useEffect, useMemo, useState } from "react";
import { mediaReconciliationService } from "../../../../service/mediaReconciliationServices";

import CustomToast from "../../../../widgets/toaster";
import SReconcileContainerFooter from "./partials/SReconcileContainerFooter";
import SReconcileContainerTable from "./partials/SReconcileContainerTable";
import MediaPrepPageHeader from "../../../common/commonComponents/mediaPrepPageHeader";
import MediaPrepHeaderSection from "../../../common/commonComponents/mediaPrepheaderSection";
import {
	ContainerReconcilationLotsResponse,
	PowderReconcilationLotsResponse,
	ReconBaseLot,
	ReconciliationPayload,
	SolutionReconcilationLotsResponse,
} from "./StartReconciliation.types";
import SReconcileMediaTable from "./partials/SReconcileMediaTable";
import SRegulatoryBox from "./partials/SRegulatoryBox";
import SReconcileMediaFooter from "./partials/SReconcileMediaFooter";
import SReconcileConfirmModal from "./partials/SReconcileConfirmModal";
import SReconcileStatCard from "./partials/SReconcileStatCard";
import SReconcileStickyHeader from "./partials/SReconcileStickyHeader";
import { useTranslation } from "react-i18next";
import ReconcileInventory from "./partials/PRContainer";

const StartMediaReconciliation: React.FC = () => {
	const { batchId, mediaType, reconStatus } = useParams<{
		batchId: string;
		mediaType: string;
		reconStatus: string;
	}>();
	const navigate = useNavigate();
	const { t } = useTranslation();

	const [lots, setLots] = useState<ReconBaseLot[]>([]);
	const [unit, setUnit] = useState<string>("G");
	const [label, setLabel] = useState<string>("QTY");
	const [batchName, setBatchName] = useState<string>("");
	const [showModal, setShowModal] = useState<boolean>(false);
	const [containerPostData, setContainerPostData] = useState<any[]>([]);
	const [reasonMap, setReasonMap] = useState<Record<string, string>>({});
	const [view, setView] = useState<"entry" | "identification">("entry");
	const [showToast, setShowToast] = useState(false);
	const [toastMessage, setToastMessage] = useState("");
	const [toastType, setToastType] = useState("");
	
	const [reasonErrors, setReasonErrors] = useState<Record<string, string>>({});
	const validateReasons = () => {
		const errors: Record<string, string> = {};

		lots.forEach((lot) => {
			const variance =
			Number(lot.physical_quantity) - Number(lot.system_quantity);

			if (variance !== 0 && !reasonMap[lot.lot_id]) {
			errors[lot.lot_id] = t("mediaReconcile.physicalReconciliation.reasonerror");
			}
		});
		setReasonErrors(errors);
		return Object.keys(errors).length === 0;
	};

	const showToastMessage = (message: string, type: string) => {
		setToastMessage(message);
		setToastType(type);
		setShowToast(true);
	};

	const getPowderLotsInfo = async () => {
		if (!batchId) return null;
		try {
			const response: PowderReconcilationLotsResponse =
				await mediaReconciliationService.getReconPowderLotList(batchId);
			if (response && response.statusCode === 200) {
				setLots(response.data.lots);
				setBatchName(response.data.batch_details.batch_name);
				setLabel("QTY");
				setUnit("G");
			}
		} catch (err) {
			console.error(
				"StartReconciliationPowder component: getLotInfo error",
				err,
			);
		}
	};

	const getSolutionLotsInfo = async () => {
		if (!batchId) return null;
		try {
			const response: SolutionReconcilationLotsResponse =
				await mediaReconciliationService.getReconSolutionLotList(batchId);
			if (response && response.statusCode === 200) {
				setLots(response.data);
				setBatchName(response.data[0].batch_name);
				setLabel("Volume");
				setUnit("mL");
			}
		} catch (err) {
			console.error(
				"StartReconciliationSolution component: getLotInfo error",
				err,
			);
		}
	};

	const getContainerLotsInfo = async () => {
		if (!batchId) return null;
		try {
			const response: ContainerReconcilationLotsResponse =
				await mediaReconciliationService.getReconContainerLotList(batchId);
			if (response && response.statusCode === 200) {
				setLots(response.data.lots);
				setBatchName(response.data.batch_details.batch_no);
				setLabel("Count");
				setUnit("units");
			}
		} catch (err) {}
	};

	const handleReasonChange = (lotId: string, reasonId: string) => {
	setReasonMap((prev) => ({
		...prev,
		[lotId]: reasonId,
	}));

	setReasonErrors((prev) => {
		const updated = { ...prev };
		delete updated[lotId];
		return updated;
	});
	};

	// Derive stats directly from lots — no separate state needed
	const stats = useMemo(() => {
		const totalSystem = lots.reduce(
			(sum, lot) => sum + Number(lot.system_quantity),
			0,
		);
		const totalPhysical = lots.reduce(
			(sum, lot) => sum + Number(lot.physical_quantity),
			0,
		);

		const lotPayload: ReconciliationPayload[] = lots
			.map((lot) => {
				const system_quantity = Number(lot.system_quantity);
				const physical_quantity = Number(lot.physical_quantity ?? 0);

				return {
					lot_id: Number(lot.lot_id),
					system_quantity,
					physical_quantity,
					difference_quantity: parseFloat(
						(physical_quantity - system_quantity).toFixed(3),
					),
					adjustment_reason_id: reasonMap[lot.lot_id] ?? "",
				};
			})
			// .filter((lot) => lot.difference_quantity !== 0);

		return {
			totalSystem,
			totalPhysical,
			totalVariance: totalPhysical - totalSystem,
			lotPayload,
		};
	}, [lots, reasonMap]);

	const handleUnitChange = (lotId: string, value: string) => {
		setLots((prevLots) =>
			prevLots.map((lot) =>
				lot.lot_id === lotId
					? { ...lot, physical_quantity: Number(value) }
					: lot,
			),
		);
	};

	const containerNegativeVarianceLots = useMemo(() => {
	return lots
		.filter(
		(lot) =>
			Number(lot.physical_quantity) <
			Number(lot.system_quantity)
		)
		.map((lot) => ({
		lot_id: lot.lot_id,
		lot: lot.lot_code,

		// values required for final payload
		system_quantity: Number(lot.system_quantity),
		physical_quantity: Number(lot.physical_quantity),
		difference_quantity:
			Number(lot.physical_quantity) -
			Number(lot.system_quantity), // keep minus sign

		// for identification screen
		variance: Math.abs(
			Number(lot.physical_quantity) -
			Number(lot.system_quantity)
		),
		}));
	}, [lots]);


	useEffect(() => {
		switch (mediaType) {
			case "powder":
				getPowderLotsInfo();
				break;
			case "solution":
				getSolutionLotsInfo();
				break;
			case "container":
				getContainerLotsInfo();
				break;
			default:
				navigate(-1);
				break;
		}
	}, []);


	const handleNavigateAfterPost = () => {
		switch (mediaType) {
			case "container":
				navigate("/reconcileContainer");
				break;

			case "powder":
				navigate("/reconcileMediaPowder");
				break;

			case "solution":
				navigate("/reconcileSolution");
				break;
		}
	};

	const handleBackToEntry = async () => {
		await getContainerLotsInfo();
		setView("entry");
	};
	if (!batchId || !mediaType) return null;
	if (view === "identification") {
		return (
			<>
				<ReconcileInventory
					onBack={handleBackToEntry}
					lotsWithNegativeVariance={containerNegativeVarianceLots}
					totalVariance={stats.totalVariance}
					batchId={batchId}
					onConfirm={(payload) => {
						console.log(payload);

						setContainerPostData(payload);
						setShowModal(true);
					}}
				/>

				{showModal && (
					<SReconcileConfirmModal
						postData={containerPostData}
						lot_code={lots[0]?.lot_code}
						batchId={batchId}
						unit={unit}
						batchName={batchName}
						totalVariance={stats.totalVariance}
						toggleModal={() => setShowModal(false)}
						mediaType={mediaType}
						handlePostSuccess={handleNavigateAfterPost}
						showToastMessage={showToastMessage}
					/>
				)}
				<CustomToast
					show={showToast}
					onClose={() => setShowToast(false)}
					delay={2500}
					message={toastMessage}
					toastType={toastType}
				/>
			</>
		);
	}
	return (
		<div className="media media-prep-scope">
			<MediaPrepHeaderSection />
			<MediaPrepPageHeader pageHeader={t("mediaReconcile.title")} />

			<div className="flex flex-col h-full bg-[#F8FAFC]">
				{/* Sticky Header */}
				<SReconcileStickyHeader mediaType={mediaType} reconStatus={reconStatus} batchName={batchName} />
				<div className="px-8 py-4 overflow-y-auto flex-1">
					{/* KPI Cards */}
					<div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-4">
						<SReconcileStatCard
							label={`${t("mediaReconcile.physicalReconciliation.system")} ${label} ${t("mediaReconcile.physicalReconciliation.total")}`}
							value={stats.totalSystem}
							unit={unit}
						/>
						<SReconcileStatCard
							label={`${t("mediaReconcile.physicalReconciliation.physical")} ${label} ${t("mediaReconcile.physicalReconciliation.total")}`}
							value={stats.totalPhysical}
							unit={unit}
						/>
						<SReconcileStatCard
							label={t(
								"mediaReconcile.physicalReconciliation.totalVariance",
							)}
							value={stats.totalVariance}
							unit={unit}
							isVariance={true}
						/>
					</div>
					<div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden mb-10">
						{mediaType === "container" ? (
							<SReconcileContainerTable
								data={lots}
								unit={unit}
								label={label}
								batchName={batchName}
								handleUnitChange={handleUnitChange}
							/>
						) : (
							<SReconcileMediaTable
								data={lots}
								mediaType={mediaType}
								unit={unit}
								label={label}
								batchName={batchName}
								handleUnitChange={handleUnitChange}
								handleReasonChange={handleReasonChange}
								reasonErrors={reasonErrors}
							/>
						)}
					</div>
					<SRegulatoryBox />
				</div>
			</div>
			<CustomToast
				show={showToast}
				onClose={() => setShowToast(false)}
				delay={2500}
				message={toastMessage}
				toastType={toastType}
			/>
			{mediaType === "container" ? (
				<SReconcileContainerFooter
					unit={unit}
					totalVariance={stats.totalVariance}
					onNext={() => setView("identification")}
				/>
			) : (
				<SReconcileMediaFooter
					unit={unit}
					totalVariance={stats.totalVariance}
					setShowModal={() => {
						if (validateReasons()) {
						setShowModal(true);
						}
					}}
				/>
			)}
			{showModal && (
			<SReconcileConfirmModal

				postData={
				mediaType === "container"
					? containerPostData
					: stats.lotPayload
				}

				lot_code={lots[0]?.lot_code}
				batchId={batchId}
				unit={unit}
				batchName={batchName}
				totalVariance={stats.totalVariance}
				toggleModal={() => setShowModal(false)}
				mediaType={mediaType}
				handlePostSuccess={handleNavigateAfterPost}
				showToastMessage={showToastMessage}
			/>
			)}
			
		</div>
	);
};

export default StartMediaReconciliation;
